import { last } from 'lodash';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Employee } from '..';
import AbsenceCategories from './AbsenceCategories';
import PersonalData from './PersonalData';
import SalariesCard from './SalariesCard';
import WorkloadCard from './WorkloadCard';
import AdditionalCompensationsCard from './AdditionalCompensationsCard';
import {
  RelatedList,
  mapEmployeeRelatedList,
} from '../../../components/EmployeeData';
import OneTimeComponsationsCard from './OneTimeCompensationsCard';
import { SimilarPeopleCard } from './SimilarPeopleCard';
import { useServerFeature } from '../../../../../hooks/useServerFeature';

type Props = {
  employee: Employee;
};

// Compensation/workload visibility is driven by the HUB feature flags of the
// current user (the manager viewing the page) — the same viewer-scoped model
// as the rest of useServerFeature. Each compensation surface has its own flag:
// base salary, recurring (additional) and one-time compensation are toggled
// independently.
const Overview: React.FC<Props> = ({ employee }) => {
  const isWorkloadVisible = useServerFeature('Employee_Workload_Details');
  const isSalariesVisible = useServerFeature('Employee_Salaries');
  const isRecurringCompensationVisible = useServerFeature(
    'Recurring_Compensation',
  );
  const isOneTimeCompensationVisible = useServerFeature(
    'One_Time_Compensation',
  );

  const currentWorkload = last(employee.currentWorkloads);
  const showWorkload = Boolean(currentWorkload) && isWorkloadVisible;
  const showSalaries = employee.salaries.length > 0 && isSalariesVisible;
  const showAddtionalCompensations =
    isRecurringCompensationVisible &&
    employee.additionalCompensations.length > 0;

  const showOneTimeCompensations =
    isOneTimeCompensationVisible && employee.oneTimeCompensations.length > 0;

  return (
    <Row>
      <Col lg={6}>
        <PersonalData employee={employee} />
      </Col>
      <Col lg={6}>
        {showWorkload && currentWorkload && (
          <WorkloadCard workload={currentWorkload} />
        )}
        {showSalaries && <SalariesCard salaries={employee.salaries} />}
        {showAddtionalCompensations && (
          <AdditionalCompensationsCard
            additionalCompensations={employee.additionalCompensations}
          />
        )}
        {showOneTimeCompensations && (
          <OneTimeComponsationsCard
            oneTimeCompensations={employee.oneTimeCompensations}
          />
        )}
        <AbsenceCategories employee={employee} />
        <SimilarPeopleCard employeeId={employee.Id} />
        {employee.data.relatedLists.map((relatedList) => (
          <RelatedList
            key={relatedList.label}
            relatedList={mapEmployeeRelatedList(relatedList)}
          />
        ))}
      </Col>
    </Row>
  );
};

export default Overview;
